import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button, buttonVariants } from '@/components/Button';
import { Input } from '@/components/Input';
import { Controller, useForm } from 'react-hook-form';
import { errorRenderer } from '@utils/utility';
import { classNames } from 'primereact/utils';
import { useDispatch, useSelector } from 'react-redux';
import teamService from '@services/manageTeams';
import styles from './TeamsTab.module.scss';
import { addNotification, notificationStates } from '@components/Notification';
import { teamActionTypes } from '@store/Team/Team.actionTypes';
import { Modal } from '@/components/Modal';
import firebase from '@/config/firebase.config';
import { getTeamMemberDetails } from '@/services/teamMemberDetails';
import { useUserCompanies } from '@/hooks/useUserCompanies';
import { Dropdown, borderedSelectStyles } from '@/components/Dropdown';
import { findSelectOptionByValue } from '@/utils/selectOptions';

const database = firebase.firestore();

function buildMembersForEdit(memberDetailsMap, editorUserId) {
  return Object.entries(memberDetailsMap || {})
    .filter(([uid, d]) => d && d.deleted !== true && uid !== editorUserId)
    .map(([uid, d]) => ({
      userUID: uid,
      userEmail: d.userEmailid,
      userName: d.userName,
      role: d.userType,
    }));
}

export const AddTeamModal = ({
  isVisible,
  onClose,
  callback,
  defaultCompanyId,
  companyId: editOwnedByCompanyId,
  teamId,
  initialTeamName,
}) => {
  const isEditMode = Boolean(teamId);

  const {
    handleSubmit,
    setValue,
    control,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({ defaultValues: { teamName: '', name: '' } });
  const dispatch = useDispatch();

  const authState = useSelector((state) => ({ ...state.auth }));
  const teamState = useSelector((state) => ({ ...state.team }));
  const userProfile = authState?.profile;
  const userUID = userProfile?.id;

  const { companyOptions, loading: loadingCompanies } =
    useUserCompanies(userUID);

  const [teamName, setTeamName] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [companyForCreate, setCompanyForCreate] = useState(null);
  const createCompanyInitDoneRef = useRef(false);

  const activeCompanyId = userProfile?.activeCompany?.companyId;

  const ownedByForEdit = editOwnedByCompanyId ?? activeCompanyId;

  const resettingForm = useCallback(() => {
    setRequesting(false);
    reset();
    setTeamName('');
    setCompanyForCreate(null);
    createCompanyInitDoneRef.current = false;
  }, [reset]);

  useEffect(() => {
    if (!isVisible) {
      resettingForm();
      return;
    }
    if (isEditMode) {
      const name = initialTeamName || '';
      setTeamName(name);
      setValue('teamName', name);
    } else {
      setTeamName('');
      setValue('teamName', '');
    }
  }, [isVisible, isEditMode, initialTeamName, setValue, resettingForm]);

  useEffect(() => {
    if (!isVisible || isEditMode) return;
    if (createCompanyInitDoneRef.current) return;
    if (!companyOptions.length) return;
    const preferredId = defaultCompanyId ?? activeCompanyId;
    const opt =
      findSelectOptionByValue(companyOptions, preferredId) ?? companyOptions[0];
    if (opt) {
      setCompanyForCreate(opt);
      createCompanyInitDoneRef.current = true;
    }
  }, [
    isVisible,
    isEditMode,
    companyOptions,
    defaultCompanyId,
    activeCompanyId,
  ]);

  function handleResponse(response) {
    if (response.success) {
      dispatch({
        type: teamActionTypes.GET_USER_ACTIVE_TEAM,
        payload: {
          userId: userProfile.id,
          companyId: userProfile?.activeCompany?.companyId,
          role: userProfile?.role,
          activeTeamid: teamState?.active?.activeTeamid,
          resetTeamsData: true,
        },
      });
      onClose();
      resettingForm();
      addNotification({
        message: response.data,
        level: notificationStates.success,
      });
      if (callback) {
        callback();
      }
    } else {
      setRequesting(false);
      addNotification({
        message: response.data,
        level: notificationStates.error,
      });
    }
  }

  const handleTeamFormSubmit = async (data) => {
    const trimmed = (data.teamName || '').trim();
    if (!trimmed) {
      addNotification({
        message: 'Team name is required',
        level: notificationStates.warning,
      });
      return;
    }

    if (isEditMode) {
      if (!ownedByForEdit || !teamId || !userProfile?.id) {
        addNotification({
          message: 'Missing company or team',
          level: notificationStates.error,
        });
        return;
      }
      try {
        setRequesting(true);
        const memberDetailsMap = await getTeamMemberDetails(database, teamId);
        if (!memberDetailsMap || Object.keys(memberDetailsMap).length === 0) {
          setRequesting(false);
          addNotification({
            message: 'Could not load team members. Try again.',
            level: notificationStates.error,
          });
          return;
        }
        const members = buildMembersForEdit(memberDetailsMap, userProfile.id);
        const response = await teamService.editTeamWithMembers({
          teamName: trimmed,
          teamId,
          ownedBy: ownedByForEdit,
          shallRemove: [],
          members,
          createdBy: {
            role: userProfile.role,
            email: userProfile.email,
            userId: userProfile.id,
            name: userProfile.name,
          },
        });
        handleResponse(response);
      } catch (e) {
        setRequesting(false);
        addNotification({
          message: e?.message || 'Failed to update team',
          level: notificationStates.error,
        });
      }
      return;
    }

    const ownedBy = companyForCreate?.value;
    if (!ownedBy) {
      addNotification({
        message: 'Please select a company',
        level: notificationStates.warning,
      });
      return;
    }

    try {
      setRequesting(true);
      const reqPayload = {
        teamName: trimmed,
        createdBy: {
          role: userProfile.role,
          email: userProfile.email,
          userId: userProfile.id,
          name: userProfile.name,
        },
        ownedBy,
      };
      const response = await teamService.createTeamWithMembers(reqPayload);
      handleResponse(response);
    } catch (e) {
      setRequesting(false);
    }
  };

  return (
    <Modal isVisible={isVisible} onClose={onClose} preventClose={requesting}>
      <div className={styles.addTeamModal}>
        <form onSubmit={handleSubmit(handleTeamFormSubmit)}>
          {!isEditMode ? (
            <div className="p-field my-3">
              <Dropdown
                label="Company *"
                value={companyForCreate}
                selectStyles={borderedSelectStyles}
                options={companyOptions}
                onChange={(option) => setCompanyForCreate(option)}
                placeholder={
                  loadingCompanies ? 'Loading companies...' : 'Select Company'
                }
                isDisabled={requesting || loadingCompanies}
              />
            </div>
          ) : null}
          <div className="p-field my-3">
            <Controller
              name="teamName"
              rules={{ required: 'Team name is required' }}
              control={control}
              render={() => (
                <>
                  <Input
                    label="Team Name"
                    placeholder="Enter Team Name"
                    value={teamName}
                    onChange={(e) => {
                      setTeamName(e.target.value);
                      setValue('teamName', e.target.value);
                      clearErrors();
                    }}
                    className={classNames(
                      { 'p-invalid': errors?.teamName },
                      'p-inputtext-sm w-100'
                    )}
                  />
                </>
              )}
            />
            {errorRenderer(errors, 'teamName')}
          </div>

          <div className={styles.buttonsContainer}>
            <Button
              variant={buttonVariants.secondary}
              text="Cancel"
              onClick={onClose}
              disabled={requesting}
            />
            <Button
              variant={buttonVariants.primary}
              type="submit"
              loading={requesting}
              text={isEditMode ? 'Save' : 'Add team'}
            />
          </div>
        </form>
      </div>
    </Modal>
  );
};
